import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Row, Col, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  LineChartOutlined,
  TeamOutlined,
  StarTwoTone,
  SafetyCertificateTwoTone,
  CalendarFilled,
  ArrowRightOutlined,
  AimOutlined,
  CompassFilled
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface PersonalDashboardProps {
  memberStatus: 'premium' | 'admin';
}

// ─── Chinese Calendar Helper ─────────────────────────────────────────────
function getChineseDate(): { day: string; month: string; year: string; stem: string; branch: string } {
  const now = new Date();
  // Simplified Chinese calendar info (in real app, fetch from backend)
  const chineseMonths = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const stems = ['Jia (甲)', 'Yi (乙)', 'Bing (丙)', 'Ding (丁)', 'Wu (戊)', 'Ji (己)', 'Geng (庚)', 'Xin (辛)', 'Ren (壬)', 'Gui (癸)'];
  const branches = ['Zi (子)', 'Chou (丑)', 'Yin (寅)', 'Mao (卯)', 'Chen (辰)', 'Si (巳)', 'Wu (午)', 'Wei (未)', 'Shen (申)', 'You (酉)', 'Xu (戌)', 'Hai (亥)'];
  const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);
  return {
    day: String(dayOfYear % 60),
    month: chineseMonths[now.getMonth()],
    year: String(now.getFullYear()),
    stem: stems[dayOfYear % 10],
    branch: branches[dayOfYear % 12],
  };
}

export const PersonalDashboard: React.FC<PersonalDashboardProps> = ({ memberStatus }) => {
  const navigate = useNavigate();
  const [chineseDate] = useState(getChineseDate);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const isAdmin = memberStatus === 'admin';

  const actions = [
    {
      title: 'Bagan BaZi Kamu',
      desc: 'Lihat bagan lahir 4 pilar — Tiang Langit, Bumi, dan 10 Dewa Ki.',
      icon: <LineChartOutlined />,
      path: '/personal/chart',
      btnText: 'Buka Bagan',
      gradient: 'linear-gradient(135deg, #0c8599 0%, #1098ad 100%)',
      accentColor: '#1098ad',
    },
    {
      title: 'Cek Kecocokan',
      desc: 'Ukur keselarasan elemen dengan pasangan, teman, atau rekan kerja.',
      icon: <TeamOutlined />,
      path: '/personal/synergy',
      btnText: 'Cek Kecocokan',
      gradient: 'linear-gradient(135deg, #2b8a3e 0%, #37b24d 100%)',
      accentColor: '#2b8a3e',
    },
  ];

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '16px' }}>
      {/* ─── HERO SECTION ─────────────────────────────────────────────── */}
      <Card
        style={{
          border: '3px solid #1a202c',
          borderRadius: '20px',
          boxShadow: '6px 6px 0 #1a202c',
          marginBottom: '28px',
          background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #1a202c 100%)',
          overflow: 'hidden',
          position: 'relative',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Decorative top-right glow */}
        <div style={{
          position: 'absolute',
          top: '-60px',
          right: '-60px',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(250,176,5,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Decorative bottom-left glow */}
        <div style={{
          position: 'absolute',
          bottom: '-80px',
          left: '-40px',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16,152,173,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px',
            flexWrap: 'wrap',
          }}>
            {/* BaZi Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #fab005, #f59f00)',
              border: '2px solid #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: 900,
              color: '#1a202c',
              boxShadow: '0 4px 12px rgba(250,176,5,0.3)',
            }}>
              八
            </div>

            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}>
                <Title level={2} style={{
                  margin: 0,
                  fontWeight: 900,
                  color: '#fff',
                  fontSize: 'clamp(1.3rem, 4vw, 1.8rem)',
                  letterSpacing: '-0.5px',
                }}>
                  Selamat Datang!
                </Title>
                {isAdmin ? (
                  <Tag color="red" style={{
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    padding: '2px 14px',
                    borderRadius: '20px',
                    border: '2px solid #ff6b6b',
                    background: 'rgba(255,107,107,0.15)',
                    color: '#ff6b6b',
                    margin: 0,
                  }}>
                    <SafetyCertificateTwoTone twoToneColor="#ff6b6b" style={{ marginRight: 4 }} />
                    Admin
                  </Tag>
                ) : (
                  <Tag color="green" style={{
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    padding: '2px 14px',
                    borderRadius: '20px',
                    border: '2px solid #69db7c',
                    background: 'rgba(105,219,124,0.15)',
                    color: '#69db7c',
                    margin: 0,
                  }}>
                    <StarTwoTone twoToneColor="#69db7c" style={{ marginRight: 4 }} />
                    Premium Member
                  </Tag>
                )}
              </div>
              <Paragraph style={{
                margin: '6px 0 0 0',
                fontSize: '0.95rem',
                fontWeight: 500,
                color: '#adb5bd',
                lineHeight: 1.5,
              }}>
                Temukan blueprint hidup kamu secara presisi — astrologi BaZi klasik bertemu kecerdasan buatan (AI).
              </Paragraph>
            </div>
          </div>

          {/* Today's BaZi Quick Info */}
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginTop: '8px',
          }}>
            <Tag style={{
              background: 'rgba(250,176,5,0.12)',
              border: '1px solid rgba(250,176,5,0.3)',
              borderRadius: '8px',
              padding: '4px 12px',
              color: '#fab005',
              fontWeight: 700,
              fontSize: '0.8rem',
            }}>
              <CalendarFilled style={{ marginRight: 6 }} />
              BaZi: {chineseDate.stem} · {chineseDate.branch}
            </Tag>
            <Tag style={{
              background: 'rgba(16,152,173,0.12)',
              border: '1px solid rgba(16,152,173,0.3)',
              borderRadius: '8px',
              padding: '4px 12px',
              color: '#66d9e8',
              fontWeight: 700,
              fontSize: '0.8rem',
            }}>
              <AimOutlined style={{ marginRight: 6 }} />
              Hari ke-{chineseDate.day} siklus 60-hari
            </Tag>
          </div>
        </div>
      </Card>

      {/* ─── QUICK STATS ROW ──────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: '28px' }}>
        {[
          { label: 'Bagan BaZi', value: '4 Pilar', icon: <CompassFilled />, color: '#1098ad', bg: '#e7f5ff', delay: 100 },
          { label: 'Elemen Kunci', value: '5 Elemen', icon: <CompassFilled />, color: '#e03131', bg: '#fff5f5', delay: 200 },
          { label: 'Hari Ini', value: new Date().toLocaleDateString('id-ID', { weekday: 'long' }), icon: <CalendarFilled />, color: '#2b8a3e', bg: '#f0fdf4', delay: 300 },
          { label: 'Status', value: isAdmin ? 'Admin' : 'Premium', icon: <StarTwoTone twoToneColor="#fab005" style={{ fontSize: 16 }} />, color: '#e67700', bg: '#fff9db', delay: 400 },
        ].map((stat, idx) => (
          <Col xs={12} sm={6} key={idx}>
            <Card
              hoverable
              style={{
                border: '2px solid #1a202c',
                borderRadius: '14px',
                boxShadow: '3px 3px 0 #1a202c',
                background: '#fff',
                opacity: loaded ? 1 : 0,
                transform: loaded ? 'translateY(0) scale(1)' : 'translateY(15px) scale(0.95)',
                transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${stat.delay}ms`,
                cursor: 'default',
              }}
              bodyStyle={{ padding: '16px' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                e.currentTarget.style.boxShadow = '6px 6px 0 #1a202c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '3px 3px 0 #1a202c';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: stat.bg,
                  border: '2px solid #1a202c',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  color: stat.color,
                  flexShrink: 0,
                }}>
                  {stat.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#868e96', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
                    {stat.label}
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#1a202c' }}>
                    {stat.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ─── FEATURE CARDS ────────────────────────────────────────────── */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
          paddingLeft: '4px',
        }}>
          <div style={{
            width: '4px',
            height: '20px',
            borderRadius: '2px',
            background: 'linear-gradient(180deg, #fab005, #f59f00)',
          }} />
          <Title level={4} style={{ margin: 0, fontWeight: 900, color: '#1a202c' }}>
            Fitur Utama
          </Title>
        </div>

        <Row gutter={[20, 20]}>
          {actions.map((act, idx) => (
            <Col xs={24} sm={12} key={idx}>
              <Card
                hoverable
                style={{
                  border: '3px solid #1a202c',
                  borderRadius: '16px',
                  boxShadow: '4px 4px 0 #1a202c',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${500 + idx * 150}ms`,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                bodyStyle={{
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px) scale(1.01)';
                  e.currentTarget.style.boxShadow = '8px 8px 0 #1a202c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '4px 4px 0 #1a202c';
                }}
                onClick={() => navigate(act.path)}
              >
                {/* Accent bar */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: act.gradient,
                }} />

                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px', flex: 1 }}>
                  {/* Icon Container */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    border: '2px solid #1a202c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: '#fff',
                    background: act.gradient,
                    boxShadow: '3px 3px 0 #1a202c',
                    flexShrink: 0,
                    transition: 'transform 0.3s ease',
                  }}
                    className="feature-icon"
                  >
                    {act.icon}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <Title level={4} style={{
                      margin: 0,
                      fontWeight: 900,
                      color: '#1a202c',
                      fontSize: '1.1rem',
                      marginBottom: '6px',
                    }}>
                      {act.title}
                    </Title>
                    <Paragraph style={{
                      margin: 0,
                      color: '#495057',
                      fontSize: '0.88rem',
                      fontWeight: 500,
                      lineHeight: 1.5,
                    }}>
                      {act.desc}
                    </Paragraph>
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  block
                  style={{
                    height: '44px',
                    background: '#fff',
                    border: '2px solid #1a202c',
                    color: '#1a202c',
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    borderRadius: '10px',
                    boxShadow: '2px 2px 0 #1a202c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = act.gradient;
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.boxShadow = '3px 3px 0 #1a202c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.color = '#1a202c';
                    e.currentTarget.style.borderColor = '#1a202c';
                    e.currentTarget.style.boxShadow = '2px 2px 0 #1a202c';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(act.path);
                  }}
                >
                  {act.btnText}
                  <ArrowRightOutlined style={{ fontSize: '14px', transition: 'transform 0.2s' }} />
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* ─── BOTTOM INSIGHT CARD ──────────────────────────────────────── */}
      <Card
        style={{
          border: '3px solid #1a202c',
          borderRadius: '16px',
          boxShadow: '4px 4px 0 #1a202c',
          marginTop: '28px',
          background: 'linear-gradient(135deg, #fff9db 0%, #fff3bf 100%)',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(15px)',
          transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 700ms',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #1a202c, #2d3748)',
            border: '2px solid #1a202c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: '#fab005',
            flexShrink: 0,
          }}>
            <CompassFilled />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Title level={5} style={{ margin: 0, fontWeight: 900, color: '#1a202c' }}>
              Filosofi BaZi: Langit · Bumi · Manusia
            </Title>
            <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.5 }}>
              Setiap bagan BaZi adalah peta langit yang unik. Dua pilar pertama (Langit) adalah potensi bawaan,
              dua pilar berikutnya (Bumi) adalah pengaruh lingkungan. Tugas kita sebagai <em>Manusia</em> adalah
              menyelaraskan keduanya.
            </Paragraph>
          </div>
          <Button
            onClick={() => navigate('/personal/chart')}
            style={{
              border: '2px solid #1a202c',
              borderRadius: '10px',
              fontWeight: 800,
              background: '#fff',
              boxShadow: '2px 2px 0 #1a202c',
              flexShrink: 0,
            }}
          >
            Lihat Bagan
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PersonalDashboard;
